import {ContentContainer} from '@coolcolduk/ui-mui';
import {ExpandMore} from '@mui/icons-material';
import {Accordion, AccordionDetails, AccordionSummary, Alert, Typography} from '@mui/material';
import {QuestionStatus} from '@cp/model';
import PageHeader from '../../../components/PageHeader';
import QuestionEditForm from '../../../components/QuestionEditForm/QuestionEditForm';
import StatusNoteDialog from '../../../components/StatusNoteDialog/StatusNoteDialog';
import {PrivateRouteProps} from '../../../config/routeType';
import LoadingPage from '../../other/LoadingPage';
import AdminQuestionViewFilters from './AdminQuestionViewFilters';
import AdminQuestionViewHeader from './AdminQuestionViewHeader';
import AdminQuestionViewNotFound from './AdminQuestionViewNotFound';
import useAdminQuestionView from './useAdminQuestionView';

export default function AdminQuestionViewPage(_props: PrivateRouteProps) {
  const {
    question,
    examModules,
    filters,
    selectedModule,
    isLoading,
    modulesLoading,
    updateLoading,
    generateGraphLoading,
    checkRulesLoading,
    checkGptLoading,
    formData,
    handleFormDataChange,
    checkResultDialogOpen,
    checkResult,
    handleCloseCheckResultDialog,
    handleBack,
    handleSave,
    handleCheckWithRules,
    handleCheckWithGpt,
    handleGenerateGraphJson,
    handleNavigateToSeedQuestion,
  } = useAdminQuestionView();

  if (isLoading) {
    return (
      <LoadingPage
        btnText="Retry Loading"
        message="Taking longer than expected? Try reloading."
        showButtonAfterMs={5000}
        onClick={handleBack}
      />
    );
  }

  if (!question) {
    return <AdminQuestionViewNotFound onBack={handleBack} />;
  }

  return (
    <ContentContainer data-testid="admin-question-view-page">
      <PageHeader title="Question View" />

      <AdminQuestionViewHeader
        onBack={handleBack}
        onCheckWithRules={handleCheckWithRules}
        onCheckWithGpt={handleCheckWithGpt}
        onGenerateGraphJson={handleGenerateGraphJson}
        onSave={handleSave}
        onNavigateToSeedQuestion={handleNavigateToSeedQuestion}
        seedQuestionId={question.seedQuestionId}
        checkRulesLoading={checkRulesLoading}
        checkGptLoading={checkGptLoading}
        generateGraphLoading={generateGraphLoading}
        updateLoading={updateLoading}
      />

      {filters && (
        <Accordion defaultExpanded={false} sx={{mb: 3}}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" sx={{color: '#1B263B'}}>
              Active Filters
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AdminQuestionViewFilters filters={filters} selectedModule={selectedModule} />
          </AccordionDetails>
        </Accordion>
      )}

      <QuestionEditForm
        formData={formData}
        onFormDataChange={handleFormDataChange}
        question={question}
        examModules={examModules}
        modulesLoading={modulesLoading}
        isEditing={!!question}
      />

      <StatusNoteDialog
        open={checkResultDialogOpen}
        onClose={handleCloseCheckResultDialog}
        statusNote={checkResult?.statusNote}
        title="Question Check Result"
        headerContent={
          checkResult?.status && (
            <Alert
              severity={
                checkResult.status === QuestionStatus.CHECKED || checkResult.status === QuestionStatus.VALIDATED
                  ? 'success'
                  : checkResult.status === QuestionStatus.FLAGGED
                    ? 'error'
                    : 'info'
              }
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Status: {checkResult.status}
              </Typography>
            </Alert>
          )
        }
      />
    </ContentContainer>
  );
}
